<template>
    <div class="contract-detail-container">
        <!-- Card Principal com Header -->
        <div class="main-card">
            <!-- Header com Gradiente (igual ao BaseTable) -->
            <div class="contract-header">
                <div class="header-content">
                    <div class="header-left">
                        <div class="header-navigation">
                            <button
                                class="btn btn-back"
                                @click="goBack"
                            >
                                <i class="fa fa-arrow-left"></i>
                                Voltar
                            </button>
                            <div class="header-titles">
                                <h1 class="contract-title">Detalhes do Contrato</h1>
                                <span class="contract-subtitle">Negociação: {{ contract.CN9_NUMERO }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="header-right">
                        <button
                            class="btn btn-outline-light btn-sm"
                            @click="contractReport"
                            :disabled="loadingReport"
                        >
                            <i
                                v-if="!loadingReport"
                                class="fa fa-file"
                            ></i>
                            <span
                                v-if="loadingReport"
                                class="fa fa-spinner fa-spin"
                            ></span>
                            {{ loadingReport ? 'Gerando...' : 'Gerar Relatório' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Conteúdo Principal -->
            <div class="contract-content">
                <!-- Grid Compacto de Informações -->
                <div class="info-grid">
                    <!-- Linha 1 -->
                    <div class="info-item-compact">
                        <span class="info-label-compact">Nº da Negociação</span>
                        <span class="info-value-compact">{{ contract.CN9_NUMERO }}</span>
                    </div>
                    <div class="info-item-compact">
                        <span class="info-label-compact">Contrato do Cliente</span>
                        <span class="info-value-compact">{{ contract.CN9_ZCONCL }}</span>
                    </div>
                    <div class="info-item-compact">
                        <span class="info-label-compact">Filial</span>
                        <span class="info-value-compact">{{ contract.CN9_FILIAL.substring(9) }}</span>
                    </div>
                    <div class="info-item-compact">
                        <span class="info-label-compact">Descrição</span>
                        <span class="info-value-compact">{{ contract.CNB_DESCRI }}</span>
                    </div>

                    <!-- Linha 2 -->
                    <div class="info-item-compact">
                        <span class="info-label-compact">% de Prazo</span>
                        <span class="info-value-compact">
                            <span class="badge bg-primary">
                                {{ calcule_date_percentage(contract.CN9_DTINIC, contract.CN9_DTFIM) }}%
                            </span>
                        </span>
                    </div>
                    <div class="info-item-compact">
                        <span class="info-label-compact">Data Inicial</span>
                        <span class="info-value-compact">{{ contract.CN9_DTINIC }}</span>
                    </div>
                    <div class="info-item-compact">
                        <span class="info-label-compact">Data Final</span>
                        <span class="info-value-compact">{{ contract.CN9_DTFIM }}</span>
                    </div>
                    <div class="info-item-compact">
                        <span class="info-label-compact">Valor Unitário</span>
                        <span class="info-value-compact text-success fw-bold">
                            R$ {{ brNumber(contract.CNB_VLUNIT) }} {{ contract.CNB_UM }}
                        </span>
                    </div>

                    <!-- Linha 3 -->
                    <div class="info-item-compact">
                        <span class="info-label-compact">Prazo em Dias</span>
                        <span class="info-value-compact">
                            {{ diff_in_days_between(contract.CN9_DTFIM, contract.CN9_DTINIC) }}
                            dias
                        </span>
                    </div>
                    <div
                        class="info-item-compact"
                        :class="{ 'alert-item': diff_in_days(contract.CN9_DTFIM) <= 60 && !['01', '06', '08'].includes(contract.CN9_SITUAC) }"
                    >
                        <span class="info-label-compact">Dias para Vencimento</span>
                        <span class="info-value-compact">
                            <span
                                :class="[
                                        'badge',
                                        diff_in_days(contract.CN9_DTFIM) <= 60 && !['01', '06', '08'].includes(contract.CN9_SITUAC)
                                          ? 'bg-danger'
                                          : diff_in_days(contract.CN9_DTFIM) <= 120
                                            ? 'bg-warning'
                                            : 'bg-success'
                                      ]"
                            >
                                {{ diff_in_days(contract.CN9_DTFIM) }} dias
                            </span>
                        </span>
                    </div>
                    <div class="info-item-compact">
                        <span class="info-label-compact">Volume Inicial</span>
                        <span class="info-value-compact">{{ getQuantity(contract.CNB_QUANT, contract.CNB_UM) }}</span>
                    </div>
                    <div class="info-item-compact">
                        <span class="info-label-compact">Volume Consumido</span>
                        <span class="info-value-compact">{{ getQuantity(contract.CNB_QTDMED, contract.CNB_UM) }}</span>
                    </div>

                    <!-- Linha 4 -->
                    <div class="info-item-compact">
                        <span class="info-label-compact">Saldo de Volume</span>
                        <span class="info-value-compact text-info fw-bold">{{ getQuantity(contract.CNB_SLDMED, contract.CNB_UM) }}
                        </span>
                    </div>
                    <div class="info-item-compact">
                        <span class="info-label-compact">% Consumido Do Volume</span>
                        <span class="info-value-compact">
                            <span class="badge bg-info">
                                {{ calcule_value_percentage(contract.CNB_QTDMED, contract.CNB_QUANT) }}%
                            </span>
                        </span>
                    </div>
                    <div class="info-item-compact"></div> <!-- Espaço vazio -->
                    <div class="info-item-compact"></div> <!-- Espaço vazio -->
                </div>
            </div>

            <!-- Seção de Pedidos Modernizada -->
            <div class="orders-section">
                <div class="orders-header">
                    <div class="orders-title-wrapper">
                        <h2 class="orders-title">Pedidos da Negociação</h2>
                        <span class="orders-count">{{ orders.length }} pedido(s)</span>
                    </div>
                    <div class="orders-actions">
                        <button
                            class="btn btn-success btn-sm"
                            @click="contractReport"
                            :disabled="loadingReport"
                        >
                            <i
                                v-if="!loadingReport"
                                class="fa fa-file-excel"
                            ></i>
                            <span
                                v-if="loadingReport"
                                class="fa fa-spinner fa-spin"
                            ></span>
                            {{ loadingReport ? 'Gerando...' : 'Exportar Excel' }}
                        </button>
                    </div>
                </div>

                <!-- Loading de Pedidos -->
                <div
                    class="orders-loading"
                    v-if="loadingOrders"
                >
                    <div class="loading-spinner"></div>
                    <p>Carregando pedidos...</p>
                </div>

                <!-- Lista de Pedidos Compacta -->
                <div
                    class="orders-list"
                    v-else-if="displayedOrders.length > 0"
                >
                    <div
                        class="order-item"
                        v-for="(order, index) in displayedOrders"
                        :key="index"
                    >
                        <div class="order-header">
                            <div class="order-info">
                                <span class="order-number">Nº pedido: {{ order.number }}</span>
                                <span class="order-measurement">Nº medição: {{ order.measurement }}</span>
                            </div>
                            <div class="order-status">
                                <span
                                    class="badge"
                                    :class="order.statusClass || 'bg-success'"
                                >{{ order.status || 'Faturado' }}
                                </span>
                            </div>
                        </div>
                        <div class="order-details">
                            <div class="detail-item">
                                <span class="detail-label">Produto:</span>
                                <span class="detail-value">{{ order.product }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Quantidade:</span>
                                <span class="detail-value">{{ order.quantity }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Valor:</span>
                                <span class="detail-value text-success fw-bold">{{ order.value }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Estado Vazio -->
                <div
                    class="empty-orders"
                    v-else
                >
                    <i class="fa fa-inbox empty-icon"></i>
                    <p class="empty-message">{{
                            orders.length === 0 ? 'Nenhum pedido encontrado para esta negociação.' : 'Nenhum pedido disponível.'
                                             }}
                    </p>
                </div>

                <!-- Paginação Simples -->
                <div
                    class="orders-pagination"
                    v-if="orders && orders.length > ordersPerPage"
                >
                    <button
                        class="btn btn-outline-secondary btn-sm"
                        @click="showLessOrders"
                        :disabled="currentPage === 1"
                    >
                        <i class="fa fa-chevron-up"></i>
                        Mostrar menos
                    </button>
                    <span class="pagination-info">({{ currentPage }} de {{ totalPages }})</span>
                    <button
                        class="btn btn-outline-secondary btn-sm"
                        @click="showMoreOrders"
                        :disabled="currentPage >= totalPages"
                    >
                        <i class="fa fa-chevron-down"></i>
                        Mostrar mais
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            loadingReport: false,
            currentPage: 1,
            ordersPerPage: 5
        };
    },
    props: {
        contract: {
            type: Object,
            required: true
        },
        orders: {
            type: Array,
            default: () => []
        },
        loadingOrders: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        displayedOrders() {
            const start = (this.currentPage - 1) * this.ordersPerPage;
            const end = start + this.ordersPerPage;
            return this.orders.slice(start, end);
        },
        totalPages() {
            return Math.ceil(this.orders.length / this.ordersPerPage);
        }
    },

    methods: {
        getQuantity(quantity, unit) {

            let qtd = quantity;
            
            if(unit == "KG") {
                qtd =  quantity / 1000;
                qtd = qtd.toFixed(2);
                
                unit = 't';
            } else {
                qtd = quantity;
                
            }

            qtd = this.brNumber(qtd);
            return qtd + ' ' + unit;
        },
        brNumber(number) {
            return new Intl.NumberFormat("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(number);
        },
        diff_in_days_between(date_end, date_start) {
            return moment(date_end, "DD/MM/YY").diff(
                moment(date_start, "DD/MM/YY").format("YYYY-MM-DD"),
                "days"
            );
        },
        diff_in_days(date) {
            let diff_in_days_total = moment(date, "DD/MM/YYYY").diff(
                moment().format("YYYY-MM-DD"),
                "days"
            );
            return Math.floor(diff_in_days_total);
        },
        calcule_value_percentage(current, total) {
            let percentage = (current * 100) / total;
            let percentage_fixed = percentage.toFixed(2);

            return percentage_fixed;
        },
        calcule_date_percentage(start_date, end_date) {
            console.log('Datas recebidas:', start_date, end_date);
            
            // Verificar se as datas existem
            if (!start_date || !end_date) {
                console.log('Datas inválidas ou vazias');
                return 0;
            }

            // Tentar diferentes formatos de data
            let start_date_moment = moment(start_date, ["DD/MM/YYYY", "DD/MM/YY", "YYYY-MM-DD"]);
            let end_date_moment = moment(end_date, ["DD/MM/YYYY", "DD/MM/YY", "YYYY-MM-DD"]);

            
            // Verificar se as datas são válidas
            if (!start_date_moment.isValid() || !end_date_moment.isValid()) {
                console.log('Datas inválidas após parsing');
                return 0;
            }

            // Calcular total de dias do período
            let total_days = end_date_moment.diff(start_date_moment, "days");
            console.log('Total de dias:', total_days);

            // Se o período for inválido (data fim antes da data início)
            if (total_days <= 0) {
                console.log('Período inválido (data fim antes da data início)');
                return 0;
            }

            // Data atual
            let current_date = moment();
            
            // Calcular dias completados desde o início
            let completed_days = current_date.diff(start_date_moment, "days");
            console.log('Dias completados:', completed_days);

            // Se ainda não começou o período
            if (completed_days < 0) {
                console.log('Período ainda não iniciou');
                return 0;
            }

            // Calcular porcentagem
            let completed_days_percentage = (completed_days * 100) / total_days;
            console.log('Porcentagem calculada:', completed_days_percentage);

            // Limitar entre 0 e 100
            let return_percentage = Math.max(0, Math.min(100, completed_days_percentage));
            return return_percentage.toFixed(2);
        },
        goBack() {
            // Método para voltar - pode usar router ou emitir evento
            this.$router.go(-1);
        },
        showMoreOrders() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
            }
        },
        showLessOrders() {
            if (this.currentPage > 1) {
                this.currentPage--;
            }
        },
        contractReport() {
            if (!this.contract) return;

            let api =
                this.$store.getters.api +
                "contracts/export-detail?CN9_FILIAL=" +
                this.contract.CN9_FILIAL.split('-')[0].trim() +
                "&CN9_NUMERO=" +
                this.contract.CN9_NUMERO;

            let self = this;
            this.loadingReport = true;

            axios
                .get(api)
                .then((response) => {
                    // create file link in browser's memory
                    const href = self.$store.getters.api + 'contracts/export-detail-file?hash=' + response.data.hash;
                    window.open(href, '_blank').focus();
                    self.loadingReport = false;
                })
                .catch((error) => {
                    this.$toast.error(
                        "Erro na solicitação de Relatório:" + (error.response?.data?.message || error.message)
                    );
                    self.loadingReport = false;
                });
        }
    },
};
</script>

<style scoped>
/* Container Principal */
.contract-detail-container {
    margin: 0;
    padding: 0;
    background: transparent;
    min-height: 100vh;
}

/* Header com Gradiente (igual ao BaseTable) */
.contract-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-left {
    display: flex;
    align-items: center;
    flex: 1;
}

.header-navigation {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: 10px;
}

.btn-back {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
}

.btn-back:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
    transform: translateY(-1px);
}

.btn-back:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.header-titles {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.contract-title {
    font-size: 24px;
    font-weight: 600;
    margin: 0;
    color: white;
}

.contract-subtitle {
    font-size: 13px;
    opacity: 0.9;
    background: rgba(255, 255, 255, 0.2);
    padding: 4px 12px;
    border-radius: 20px;
    display: inline-block;
    width: fit-content;
}

.header-right {
    display: flex;
    gap: 8px;
}

.btn-outline-light {
    border: 2px solid white;
    background: transparent;
    color: white;
    transition: all 0.3s ease;
}

.btn-outline-light:hover {
    background: white;
    color: #667eea;
    transform: translateY(-1px);
}

.btn-outline-light:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.orders-actions .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Card Principal */
.main-card {
    margin: 0;
    border-radius: 0;
    box-shadow: none;
    overflow: hidden;
    border: none;
    background: white;
    min-height: 100vh;
}

/* Conteúdo Principal */
.contract-content {
    background: white;
    border-radius: 0;
}

/* Grid Compacto de Informações */
.info-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1px;
    background: #e9ecef;
}

.info-item-compact {
    background: white;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    transition: all 0.3s ease;
    min-height: 60px;
    justify-content: center;
}

.info-item-compact:hover {
    background: #f8f9fa;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.info-item-compact.alert-item {
    background: #fff3cd;
    animation: pulse-warning 2s infinite;
}

@keyframes pulse-warning {
    0% {
        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.4);
    }

    70% {
        box-shadow: 0 0 0 8px rgba(255, 193, 7, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    }
}

.info-label-compact {
    font-size: 10px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1.2;
}

.info-value-compact {
    font-size: 13px;
    font-weight: 500;
    color: #495057;
    line-height: 1.3;
    word-break: break-word;
}

/* Seção de Pedidos */
.orders-section {
    background: white;
    margin: 0;
    margin-top: 1px;
    border-radius: 0;
    box-shadow: none;
    overflow: hidden;
    border: none;
    border-top: 1px solid #e9ecef;
}

.orders-header {
    background: #f8f9fa;
    padding: 16px 20px;
    border-bottom: 1px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.orders-title-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
}

.orders-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    color: #495057;
}

.orders-count {
    background: #667eea;
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
}

/* Lista de Pedidos */
.orders-list {
    padding: 0;
}

.order-item {
    border-bottom: 1px solid #f1f3f4;
    padding: 16px 20px;
    transition: all 0.3s ease;
}

.order-item:hover {
    background: #f8f9fa;
}

.order-item:last-child {
    border-bottom: none;
}

.order-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.order-info {
    display: flex;
    gap: 16px;
}

.order-number,
.order-measurement {
    font-size: 12px;
    font-weight: 600;
    color: #495057;
}

.order-status .badge {
    font-size: 11px;
    padding: 4px 8px;
}

.order-details {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 16px;
    margin-top: 8px;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.detail-label {
    font-size: 10px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.detail-value {
    font-size: 12px;
    font-weight: 500;
    color: #495057;
}

/* Estado Vazio */
.empty-orders {
    padding: 60px 20px;
    text-align: center;
    color: #6c757d;
}

.empty-icon {
    font-size: 48px;
    opacity: 0.5;
    margin-bottom: 16px;
}

.empty-message {
    font-size: 14px;
    margin: 0;
}

/* Loading de Pedidos */
.orders-loading {
    padding: 60px 20px;
    text-align: center;
    color: #6c757d;
}

.orders-loading p {
    margin: 16px 0 0 0;
    font-size: 14px;
}


/* Loading de Pedidos */
.orders-loading {
    padding: 60px 20px;
    text-align: center;
    color: #6c757d;
}

.orders-loading p {
    margin: 16px 0 0 0;
    font-size: 14px;
}


@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

/* Paginação */
.orders-pagination {
    padding: 16px 20px;
    border-top: 1px solid #e9ecef;
    background: #f8f9fa;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
}

.pagination-info {
    font-size: 12px;
    color: #6c757d;
    margin: 0 8px;
}

/* Badges */
.badge {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
    transition: all 0.3s ease;
}

.badge:hover {
    transform: scale(1.05);
}

/* Cores */
.text-success {
    color: #28a745 !important;
}

.text-info {
    color: #17a2b8 !important;
}

.fw-bold {
    font-weight: 600 !important;
}

/* Responsividade */
@media (max-width: 768px) {
    .contract-header {
        padding: 16px;
    }

    .header-content {
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }

    .header-navigation {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .contract-title {
        font-size: 20px;
    }

    .main-card,
    .orders-section {
        margin: 0;
    }

    .info-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .info-item-compact {
        padding: 10px 12px;
        min-height: 50px;
    }

    .info-label-compact {
        font-size: 9px;
    }

    .info-value-compact {
        font-size: 12px;
    }

    .orders-header {
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }

    .order-details {
        grid-template-columns: 1fr;
        gap: 8px;
    }

    .order-info {
        flex-direction: column;
        gap: 4px;
    }
}

@media (max-width: 576px) {
    .contract-header {
        padding: 12px;
    }

    .main-card,
    .orders-section {
        margin: 0;
    }

    .info-grid {
        grid-template-columns: 1fr;
    }

    .info-item-compact {
        padding: 8px 12px;
    }

    .orders-title {
        font-size: 16px;
    }

    .order-item {
        padding: 12px 16px;
    }

    .orders-pagination {
        padding: 12px 16px;
    }
}

/* Animações */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.main-card,
.orders-section {
    animation: slideIn 0.3s ease-out;
}

/* Melhorias de acessibilidade */
.info-item-compact:focus-within,
.order-item:focus-within {
    outline: 2px solid #667eea;
    outline-offset: 2px;
}

/* Scrollbar personalizada */
::-webkit-scrollbar {
    width: 8px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style>